import { CSS_CLASSES, MARKER_CONFIG } from "../config/constants.js";
import { Logger } from "./Logger.js";

/**
 * Utility class for DOM manipulation and element creation
 */
export class DOMUtils {
	constructor() {
		this.logger = Logger.createLogger("DOMUtils");
	}

	/**
	 * Create a marker element
	 * @param {number} number - Marker number
	 * @param {number} dbId - Database ID of associated object
	 * @param {string} userImageLink - URL of user's profile image (optional)
	 * @param {string} userName - User's name for alt text and initials (optional)
	 * @param {string} comment - Comment text (optional)
	 * @returns {HTMLElement} Created marker element
	 */
	createMarkerElement(
		number,
		dbId,
		userImageLink = null,
		userName = "",
		comment = ""
	) {
		const markerDiv = document.createElement("div");
		markerDiv.className = `${CSS_CLASSES.MARKER} ${CSS_CLASSES.MARKER_APPEARING}`;
		markerDiv.setAttribute("data-marker-id", number);
		markerDiv.setAttribute("data-db-id", dbId);

		// Add comment data if provided
		if (comment && comment.trim()) {
			markerDiv.setAttribute("data-comment", comment.trim());
			markerDiv.classList.add("custom-marker--has-comment");
		}

		markerDiv.title = `Marker ${number} - Object ID: ${dbId}${userName ? ` - ${userName}` : ""}${comment ? ` - Comment: ${comment}` : ""}`;

		// Create marker content based on available user data
		if (userImageLink) {
			// Create image-based marker (user avatar)
			const imageDiv = document.createElement("div");
			imageDiv.className = "custom-marker__image";
			imageDiv.style.backgroundImage = `url(${userImageLink})`;
			imageDiv.style.backgroundSize = "cover";
			imageDiv.style.backgroundPosition = "center";
			imageDiv.style.backgroundRepeat = "no-repeat";
			imageDiv.style.width = "100%";
			imageDiv.style.height = "100%";
			imageDiv.style.borderRadius = "50%";
			markerDiv.appendChild(imageDiv);
			markerDiv.classList.add("custom-marker--image");
		} else if (userName) {
			// Create initials-based marker
			const initials = this.getUserInitials(userName);
			markerDiv.textContent = initials;
			markerDiv.classList.add("custom-marker--text");
		} else {
			// Fallback to number-based marker
			markerDiv.textContent = number;
			markerDiv.classList.add("custom-marker--text");
		}

		// Set initial styles
		markerDiv.style.position = "absolute";
		markerDiv.style.left = "-1000px";
		markerDiv.style.top = "-1000px";
		markerDiv.style.visibility = "hidden";
		markerDiv.style.display = "flex";

		this.logger.debug(
			`Created ${userImageLink ? "image" : userName ? "initials" : "number"} marker element ${number} for dbId ${dbId}${comment ? " with comment" : ""}`
		);
		return markerDiv;
	}

	/**
	 * Extract user initials from full name
	 * @param {string} userName - Full name of the user
	 * @returns {string} User initials (max 2 characters)
	 */
	getUserInitials(userName) {
		if (!userName || typeof userName !== "string") {
			return "";
		}

		const nameParts = userName.trim().split(/\s+/);
		if (nameParts.length === 1) {
			// Single name - take first 2 characters
			return nameParts[0].substring(0, 2).toUpperCase();
		} else {
			// Multiple names - take first character of first and last name
			const firstInitial = nameParts[0].charAt(0);
			const lastInitial = nameParts[nameParts.length - 1].charAt(0);
			return (firstInitial + lastInitial).toUpperCase();
		}
	}

	/**
	 * Position a marker element on screen
	 * @param {HTMLElement} element - Marker element
	 * @param {Object} screenPos - Screen position {x, y}
	 * @param {Object} options - Positioning options
	 */
	positionElement(element, screenPos, options = {}) {
		const {
			isVisible = true,
			isOnScreen = true,
			centerX = true,
			centerY = true,
		} = options;

		if (!this.isValidPosition(screenPos)) {
			this.logger.error("Invalid screen position provided", screenPos);
			return;
		}

		// Calculate position accounting for element dimensions
		let left = screenPos.x;
		let top = screenPos.y;

		// if (centerX && element.offsetWidth > 0) {
		//   left -= element.offsetWidth / 2;
		// }
		if (centerY && element.offsetHeight > 0) {
			top -= element.offsetHeight;
		}

		// Apply position
		element.style.left = Math.floor(left) + "px";
		element.style.top = Math.floor(top) + "px";

		// Set visibility
		const shouldShow = isVisible && isOnScreen;
		element.style.display = shouldShow ? "flex" : "none";
		element.style.visibility = shouldShow ? "visible" : "hidden";
	}

	/**
	 * Wait for an element to be ready (have dimensions)
	 * @param {HTMLElement} element - Element to check
	 * @param {Function} callback - Callback when ready
	 * @param {number} maxAttempts - Maximum attempts
	 * @returns {Promise<void>}
	 */
	waitForElementReady(element, callback, maxAttempts = 50) {
		return new Promise((resolve) => {
			let attempts = 0;

			const checkElement = () => {
				attempts++;

				if (element.offsetWidth > 0 && element.offsetHeight > 0) {
					this.logger.debug(`Element ready after ${attempts} attempts`);
					callback();
					resolve();
					return;
				}

				if (attempts >= maxAttempts) {
					this.logger.warn(
						`Element not ready after ${maxAttempts} attempts, proceeding anyway`
					);
					callback();
					resolve();
					return;
				}

				requestAnimationFrame(checkElement);
			};

			requestAnimationFrame(checkElement);
		});
	}

	/**
	 * Add an element to the viewer container
	 * @param {HTMLElement} element - Element to add
	 * @param {HTMLElement} container - Container element
	 */
	addToContainer(element, container) {
		if (!container) {
			this.logger.error("No container provided for element");
			return;
		}

		container.appendChild(element);
		this.logger.debug("Added element to container");

		// Remove appearing class after animation
		setTimeout(() => {
			element.classList.remove(CSS_CLASSES.MARKER_APPEARING);
		}, MARKER_CONFIG.ANIMATION_DURATION);
	}

	/**
	 * Remove an element from its parent
	 * @param {HTMLElement} element - Element to remove
	 */
	removeElement(element) {
		if (element && element.parentNode) {
			element.parentNode.removeChild(element);
			this.logger.debug("Removed element from DOM");
		}
	}

	/**
	 * Validate screen position
	 * @param {Object} position - Position object {x, y}
	 * @returns {boolean} True if valid
	 */
	isValidPosition(position) {
		return (
			position &&
			typeof position.x === "number" &&
			typeof position.y === "number" &&
			!isNaN(position.x) &&
			!isNaN(position.y)
		);
	}

	/**
	 * Check if position is within screen bounds
	 * @param {Object} position - Position {x, y}
	 * @param {HTMLElement} container - Container element
	 * @param {number} tolerance - Tolerance in pixels
	 * @returns {boolean} True if on screen
	 */
	isPositionOnScreen(
		position,
		container,
		tolerance = MARKER_CONFIG.POSITION_TOLERANCE
	) {
		if (!this.isValidPosition(position) || !container) {
			return false;
		}

		const bounds = container.getBoundingClientRect();
		return (
			position.x >= -tolerance &&
			position.x <= bounds.width + tolerance &&
			position.y >= -tolerance &&
			position.y <= bounds.height + tolerance
		);
	}

	/**
	 * Add click handler to element
	 * @param {HTMLElement} element - Element to add handler to
	 * @param {Function} handler - Click handler function
	 */
	addClickHandler(element, handler) {
		element.addEventListener("click", (e) => {
			e.stopPropagation();
			handler(e);
		});
	}

	/**
	 * Set marker as active (green state)
	 * @param {HTMLElement} element - Marker element
	 */
	setMarkerActive(element) {
		if (element && !element.classList.contains(CSS_CLASSES.MARKER_ACTIVE)) {
			element.classList.add(CSS_CLASSES.MARKER_ACTIVE);
		}
	}

	/**
	 * Remove active state from marker
	 * @param {HTMLElement} element - Marker element
	 */
	setMarkerInactive(element) {
		if (element && element.classList.contains(CSS_CLASSES.MARKER_ACTIVE)) {
			element.classList.remove(CSS_CLASSES.MARKER_ACTIVE);
		}
	}

	/**
	 * Set marker as hovered (for highlighting from marker list)
	 * @param {HTMLElement} element - Marker element
	 */
	setMarkerHovered(element) {
		if (element && !element.classList.contains(CSS_CLASSES.MARKER_HOVERED)) {
			element.classList.add(CSS_CLASSES.MARKER_HOVERED);
		}
	}

	/**
	 * Remove hovered state from marker
	 * @param {HTMLElement} element - Marker element
	 */
	setMarkerUnhovered(element) {
		if (element && element.classList.contains(CSS_CLASSES.MARKER_HOVERED)) {
			element.classList.remove(CSS_CLASSES.MARKER_HOVERED);
		}
	}

	/**
	 * Check if marker is active
	 * @param {HTMLElement} element - Marker element
	 * @returns {boolean} True if marker is active
	 */
	isMarkerActive(element) {
		return element && element.classList.contains(CSS_CLASSES.MARKER_ACTIVE);
	}
}
